import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { App } from 'antd'
import { handleApiError } from '../utils/errorHandler'
import { FormBaseType } from '../types/form'
import type {
    AuthNavigationState,
    AuthRedirectTarget,
} from '../utils/navigationState'

type AuthRedirectResolver<TResult> =
    | string
    | AuthRedirectTarget
    | ((result: TResult) => string | AuthRedirectTarget)

interface AuthFormHookReturn {
    loading: boolean
    error: string | null
    handleSubmit: <T extends FormBaseType, TResult>(
        authAction: (values: T) => Promise<TResult>,
        values: T,
        successMessage?: string,
        redirectTo?: AuthRedirectResolver<TResult>
    ) => Promise<void>
    state: AuthNavigationState | null
}

export function useAuthForm(defaultRedirect: string = '/'): AuthFormHookReturn {
    const { message } = App.useApp()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const navigate = useNavigate()
    const location = useLocation()
    const state = location.state as AuthNavigationState | null

    const handleSubmit = async <T extends FormBaseType, TResult>(
        authAction: (values: T) => Promise<TResult>,
        values: T,
        successMessage: string = '鎿嶄綔鎴愬姛',
        redirectTo: AuthRedirectResolver<TResult> = defaultRedirect
    ): Promise<void> => {
        setError(null)
        setLoading(true)

        try {
            const result = await authAction(values)
            const resolvedRedirect =
                typeof redirectTo === 'function'
                    ? redirectTo(result)
                    : redirectTo
            const nextRedirect: AuthRedirectTarget =
                typeof resolvedRedirect === 'string'
                    ? { to: resolvedRedirect }
                    : resolvedRedirect

            message.success(successMessage)
            navigate(nextRedirect.to, {
                state: {
                    ...nextRedirect.state,
                    message: nextRedirect.state?.message ?? successMessage,
                },
            })
        } catch (err: unknown) {
            const errorDetails = handleApiError(err)
            setError(errorDetails.message)
        } finally {
            setLoading(false)
        }
    }

    return {
        loading,
        error,
        handleSubmit,
        state,
    }
}
